import { Component, Input } from '@angular/core';
import { AccountUsageLimitDto } from '../../models/account-usage.model';
import { LanguageService } from '../../../../services/language/language.service';

export type LimitVisualState =
  | 'unlimited'
  | 'unavailable'
  | 'blocking'
  | 'overage_allowed'
  | 'warning'
  | 'healthy';

@Component({
  selector: 'app-usage-limit-card',
  templateUrl: './usage-limit-card.component.html',
  styleUrls: ['./usage-limit-card.component.scss'],
  standalone: false
})
export class UsageLimitCardComponent {
  @Input() limit!: AccountUsageLimitDto;

  constructor(private readonly languageService: LanguageService) {}

  get state(): LimitVisualState {
    const limit = this.limit;
    if (this.hasUnavailableFlag()) {
      return 'unavailable';
    }
    if (limit.limitValue == null) {
      return 'unlimited';
    }
    if (limit.overLimit && !limit.overageAllowed) {
      return 'blocking';
    }
    if (limit.overLimit && limit.overageAllowed) {
      return 'overage_allowed';
    }
    if ((limit.usagePercent ?? 0) >= 80) {
      return 'warning';
    }
    return 'healthy';
  }

  get displayPercent(): number | null {
    if (this.state === 'unlimited' || this.state === 'unavailable') {
      return null;
    }
    const pct = this.limit.usagePercent;
    if (pct == null || !Number.isFinite(pct)) {
      return null;
    }
    return Math.max(0, Math.min(100, pct));
  }

  get progressWidth(): number {
    return this.displayPercent ?? 0;
  }

  get consumedDisplay(): string {
    if (this.state === 'unavailable') {
      return '—';
    }
    return this.formatNumber(this.limit.consumedQuantity);
  }

  get limitValueDisplay(): string {
    if (this.limit.limitValue == null) {
      return this.translate('usage.unlimited');
    }
    return this.formatNumber(this.limit.limitValue);
  }

  get enforcementLabel(): string {
    const key = `usage.enforcement.${this.limit.enforcementMode}`;
    const translated = this.languageService.translate(key);
    return translated && translated !== key ? translated : this.limit.enforcementMode;
  }

  get unitLabel(): string {
    return this.limit.unit ? this.formatUnit(this.limit.unit) : '';
  }

  private hasUnavailableFlag(): boolean {
    const flags = this.limit?.dataQuality ?? [];
    return flags.includes('source_unavailable') || flags.includes('usage_not_evaluated');
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '—';
    }
    try {
      return value.toLocaleString(this.localeForLang());
    } catch {
      return String(value);
    }
  }

  private formatUnit(unit: string): string {
    const limitSpecificKey = `usage.unit.${this.limit.limitCode}`;
    const limitSpecificTranslation = this.languageService.translate(limitSpecificKey);
    if (limitSpecificTranslation && limitSpecificTranslation !== limitSpecificKey) {
      return limitSpecificTranslation;
    }

    const key = `usage.unit.${unit}`;
    const translated = this.languageService.translate(key);
    return translated && translated !== key ? translated : unit;
  }

  private translate(key: string): string {
    const translated = this.languageService.translate(key);
    return translated && translated !== key ? translated : key;
  }

  private localeForLang(): string {
    const lang = this.languageService.getCurrentLang();
    const map: Record<string, string> = { es: 'es-CO', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', ja: 'ja-JP' };
    return map[lang] || 'es-CO';
  }
}
